/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { X, AlertTriangle, DollarSign, Key, Cpu } from 'lucide-react'

interface ErrorModalProps {
  isOpen: boolean
  title: string
  message: string
  errorType: 'payment' | 'api_key' | 'rate_limit' | 'model_error' | 'generic'
  onClose: () => void
  actionUrl?: string
  actionLabel?: string
}

export function ErrorModal({
  isOpen,
  title,
  message,
  errorType,
  onClose,
  actionUrl,
  actionLabel,
}: ErrorModalProps) {
  if (!isOpen) return null

  const getIcon = () => {
    switch (errorType) {
      case 'payment':
        return <DollarSign className="w-12 h-12 text-yellow-400" />
      case 'api_key':
        return <Key className="w-12 h-12 text-red-400" />
      case 'rate_limit':
        return <AlertTriangle className="w-12 h-12 text-orange-400" />
      case 'model_error':
        return <Cpu className="w-12 h-12 text-blue-400" />
      default:
        return <AlertTriangle className="w-12 h-12 text-red-400" />
    }
  }

  const getBackgroundColor = () => {
    switch (errorType) {
      case 'payment':
        return 'bg-yellow-500/10 border-yellow-500/30'
      case 'api_key':
        return 'bg-red-500/10 border-red-500/30'
      case 'rate_limit':
        return 'bg-orange-500/10 border-orange-500/30'
      case 'model_error':
        return 'bg-blue-500/10 border-blue-500/30'
      default:
        return 'bg-red-500/10 border-red-500/30'
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background-secondary border border-border rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-background-tertiary rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className={`flex flex-col items-center text-center p-6 rounded-lg border ${getBackgroundColor()}`}>
            {getIcon()}
            <p className="mt-4 text-sm text-foreground-secondary whitespace-pre-wrap">
              {message}
            </p>
          </div>

          {/* Action Button */}
          {actionUrl && actionLabel && (
            <div className="mt-6 flex justify-center">
              <a
                href={actionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-accent hover:bg-accent/80 text-background rounded transition-colors font-medium"
              >
                {actionLabel}
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-background-tertiary hover:bg-background rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

